/**
 * buildOrgTree - pure function that turns the flat `nodes` array returned by
 * `GET /api/org-chart` into a forest.
 *
 * Each node gets a `children` array attached (sorted by first_name, then
 * last_name, then id - the same order the server already returns `nodes` in,
 * but we sort defensively here rather than trust that ordering survives).
 * A node whose `manager_id` is null, or points at someone not present in the
 * payload, is a top-level node. The latter case covers scoped mode: the top
 * of the caller's spine can have a real manager the server never sent.
 *
 * A top-level node with zero children (no manager AND no reports - typically
 * an unlinked employee record) is not a real tree, so it is split out into
 * `orphans` rather than `roots`. The page groups those into a separate
 * "No reporting chain" panel instead of rendering each as its own one-person
 * tree next to the real org chart.
 *
 * @param {Array} nodes - flat org-chart nodes from the API response.
 * @returns {{ roots: Array, orphans: Array, byId: Object }}
 */
const compareNodes = (a, b) => {
  if (a.first_name !== b.first_name) return a.first_name < b.first_name ? -1 : 1;
  if (a.last_name !== b.last_name) return a.last_name < b.last_name ? -1 : 1;
  return a.id - b.id;
};

// Total nodes in `node`'s subtree, not counting `node` itself. Exported so
// callers (the page's scoped-mode subtitle) can reuse this instead of
// re-walking the tree with a second copy of the same logic.
export const countDescendants = (node) => (node.children || [])
  .reduce((sum, child) => sum + 1 + countDescendants(child), 0);

// Biggest tree first, so a company's main branch never gets buried under a
// small one just because its root's name sorts earlier.
const compareRoots = (a, b) => {
  const byDescendants = countDescendants(b) - countDescendants(a);
  if (byDescendants !== 0) return byDescendants;
  return compareNodes(a, b);
};

const buildOrgTree = (nodes) => {
  const byId = {};

  (nodes || []).forEach((node) => {
    byId[node.id] = { ...node, children: [] };
  });

  const topLevel = [];
  Object.values(byId).forEach((node) => {
    const parent = node.manager_id != null ? byId[node.manager_id] : null;
    if (parent) {
      parent.children.push(node);
    } else {
      topLevel.push(node);
    }
  });

  Object.values(byId).forEach((node) => {
    node.children.sort(compareNodes);
  });

  const roots = [];
  const orphans = [];
  topLevel.forEach((node) => {
    (node.children.length > 0 ? roots : orphans).push(node);
  });

  roots.sort(compareRoots);
  orphans.sort(compareNodes);

  return { roots, orphans, byId };
};

// Key used for the null/absent-department bucket, both in the returned
// group's own `key` and in the `org-branch-dept-<parentId>-<departmentId>`
// testid the tree renders above it (Task 7).
export const NO_DEPARTMENT_KEY = 'none';

/**
 * groupChildrenByDepartment - pure helper that clusters a node's direct
 * children (or a leader's direct reports, in the export mini chart) into
 * department groups, for the "department label above each branch" feature.
 *
 * Group order: descending group size first, then department name
 * (localeCompare) as the tie-break - a node's biggest department reads as
 * its main branch, with same-size departments falling into a stable,
 * predictable alphabetical order. A missing/empty `department_name` becomes
 * its own "No department" group, sorted into that same order like any other
 * named department rather than always pinned first or last.
 *
 * "Has a department" is decided by `department_name` (a non-empty string),
 * not `department_id` - the same predicate `OrgNode`'s own card pill already
 * uses (`node.department_name ? ... : 'No department'`). A row with a
 * `department_id` set but no `department_name` (bad/incomplete data) still
 * falls into the "No department" bucket, matching what its own card
 * already shows, rather than becoming a coloured group with an empty
 * caption - the colour and the caption text must agree.
 *
 * Within a group, children keep the exact order they arrived in - callers
 * pass children already ordered by `buildOrgTree` (first_name, last_name,
 * id) or a headcount-ordered slice; re-sorting here would silently discard
 * that ordering decision.
 *
 * @param {Array} children - a node's `children` (or a leader's `reports`).
 * @returns {Array<{ key: string, department_id: number|null, department_name: string, children: Array }>}
 */
export const groupChildrenByDepartment = (children) => {
  const list = children || [];
  const groups = [];
  const byKey = new Map();

  list.forEach((child) => {
    const hasDepartment = Boolean(child.department_name);
    const key = hasDepartment ? String(child.department_id) : NO_DEPARTMENT_KEY;

    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        department_id: hasDepartment ? child.department_id : null,
        department_name: hasDepartment ? child.department_name : 'No department',
        children: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.children.push(child);
  });

  return groups.sort((a, b) => {
    if (b.children.length !== a.children.length) return b.children.length - a.children.length;
    return a.department_name.localeCompare(b.department_name);
  });
};

export default buildOrgTree;
